<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { Edge, Node, useVueFlow, VueFlow } from '@vue-flow/core';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useRoomStore } from '@/store/entities/roomStore';
import RoomNode from '@/components/Canvas/RoomNode.vue';
import AddRoomNode from '@/components/Canvas/AddRoomNode.vue';
import RoomGroupCanvasHeader from '@/components/Canvas/RoomGroupCanvasHeader.vue';
import { Room } from '@/models';
import { useLayoutStore } from '@/store/layout/layoutStore';
import RoomEdge from '@/components/Canvas/RoomEdge.vue';

const {
  nodesDraggable,
  panOnDrag,
  fitView,
  updateNodeInternals,
  onNodeClick,
  onInit,
  onNodesInitialized,
} = useVueFlow();
const currentRoomgroup = computed(() => {
  return useRoomGroupStore().getCurrentRoomGroup;
});
const allRooms = computed(() => {
  if (!currentRoomgroup.value?.id) return [];
  return useRoomStore().getRoomsByRoomGroupId(currentRoomgroup.value.id);
});

const nodeMap = ref<Record<string, RoomTreeNode>>({});
const nodes = ref<Array<Node>>([]);
const edges = ref<Array<Edge>>([]);
const container = ref<any>(null);
const selectedNode = computed(() => {
  return useLayoutStore().currentNode as Room;
});
const rootNode = ref<RoomTreeNode | null>(null);

onMounted(() => {
  nodesDraggable.value = false;
  mapNodes();
  drawNodes();
  if (rootNode.value?.data) {
    selectNode(rootNode.value?.data);
  }
});

onNodesInitialized(() => {
  drawEdges();
});

const fitViewSettings = {
  padding: 0.1,
  maxZoom: 1,
  offset: { x: 0, y: 100 },
};
onInit(({ fitView }) => {
  fitView({
    nodes: nodes.value.map((node) => node.id),
    ...fitViewSettings,
  });
});

watch(
  () => allRooms.value,
  () => {
    mapNodes();
    drawNodes();
    drawEdges();
    selectNode(selectedNode.value);
  },
  {
    deep: true,
  }
);

onNodeClick((event) => {
  if (event.node.id !== 'addRoom') {
    const clickedNode = event.node;
    selectNode(clickedNode.data);
  }
});

type RoomTreeNode = {
  id: string;
  data?: Room;
  children: Array<RoomTreeNode>;
  depth: number;
  width: number;
  height: number;
  x: number;
  y: number;
};

const addRoomNode = {
  id: 'addRoom',
  type: 'add-room',
  children: [],
  depth: 0,
  width: 150,
  height: 136,
  x: 0,
  y: 0,
};

const mapNodes = () => {
  nodeMap.value = {};
  allRooms.value.forEach((room) => {
    nodeMap.value[room.id] = {
      id: room.id,
      data: room,
      children: [],
      depth: 0,
      width: 150,
      height: 136,
      x: 0,
      y: 0,
    };
  });
  nodeMap.value['addRoom'] = addRoomNode;
  allRooms.value.forEach((room) => {
    const node = nodeMap.value[room.id];

    if (room.parentRoomId && nodeMap.value[room.parentRoomId]) {
      nodeMap.value[room.parentRoomId].children.push(node);
    } else {
      rootNode.value = node;
    }
  });
};

const drawNodes = () => {
  const viewHeight = container.value?.viewportRef?.clientHeight;
  const margin = 200;
  const verticalSpacing = 175;
  const horizontalSpacing = swapMode.value ? 75 : 50;

  let xCounter = 0;

  const assignDepth = (node: RoomTreeNode, depth: number) => {
    node.depth = depth;
    node.children.forEach((child) => assignDepth(child, depth + 1));
  };
  if (!rootNode.value) return;
  assignDepth(rootNode.value, 0);

  const assignPositions = (node: RoomTreeNode) => {
    if (node.children.length > 0) {
      node.children.sort(
        (a, b) => (a.data?.ordinal ?? Infinity) - (b.data?.ordinal ?? Infinity)
      );
      node.children.forEach((child) => assignPositions(child));

      node.width = node.children.reduce((sum, child, index) => {
        return sum + child.width + (index > 0 ? horizontalSpacing : 0);
      }, 0);

      const firstChild = node.children[0];
      const lastChild = node.children[node.children.length - 1];
      node.x =
        (firstChild.x + lastChild.x + lastChild.width) / 2 - node.width / 2;
    } else {
      node.width = 150;
      node.x = xCounter * (node.width + horizontalSpacing);
      xCounter++;
    }

    node.y = viewHeight - margin - node.depth * verticalSpacing;
  };
  assignPositions(rootNode.value);

  nodes.value = Object.values(nodeMap.value).map(
    (node) =>
      ({
        id: node.id,
        type: node.id === 'addRoom' ? 'add-room' : 'room',
        children: node.children,
        data: {
          ...node.data,
          children: node.children,
        },
        width: node.width,
        height: 136,
        position: { x: node.x, y: node.y },
      }) as Node
  );
};

const drawEdges = () => {
  edges.value = allRooms.value.reduce((acc, room) => {
    if (!room.parentRoomId) return acc;
    if (nodeMap.value[room.parentRoomId] && nodeMap.value[room.id]) {
      acc.push({
        id: `edge-${room.parentRoomId}-${room.id}`,
        source: room.parentRoomId,
        target: room.id,
        sourceHandle: room.id,
        targetHandle: 'b',
        data: {
          sourceFloorLevel: useRoomStore().getRoomById(room.parentRoomId)
            .floorLevel,
          targetFloorLevel: room.floorLevel,
        },
        type: 'custom',
      });
    }
    return acc;
  }, [] as Array<Edge>);

  if (
    selectedNode.value?.id &&
    nodeMap.value[selectedNode.value.id] &&
    nodeMap.value['addRoom']
  ) {
    edges.value.push({
      id: `edge-${selectedNode.value.id}-addRoom`,
      source: selectedNode.value.id,
      target: 'addRoom',
      sourceHandle: 'addRoom',
      targetHandle: 'addRoomTarget',
      style: { strokeDasharray: 6 },
      type: 'step',
    });
  }
};

const selectNode = (node: Room) => {
  useLayoutStore().changeCurrentNode(node);
  const selectedId = selectedNode.value?.id;
  if (selectedId && nodeMap.value[selectedId]) {
    Object.values(nodeMap.value).forEach((node) => {
      node.children = node.children.filter((child) => child.id !== 'addRoom');
    });
    nodeMap.value[selectedId].children.push(addRoomNode);
  }
  drawNodes();
  setTimeout(() => {
    drawEdges();
    updateNodeInternals();
  }, 1);
};

const swapMode = ref(false);
const toggleSwapMode = () => {
  swapMode.value = !swapMode.value;
  drawNodes();
};
</script>

<template>
  <RoomGroupCanvasHeader />
  <div
    class="fixed right-10 top-28 z-10 flex gap-2 md:bottom-10 md:left-32 md:top-auto"
  >
    <button
      class="rounded-xl bg-light-1 px-3 py-2 dark:bg-dark-2"
      @click="() => fitView(fitViewSettings)"
    >
      <em class="mdi mdi-crosshairs-gps text-xl"></em>
    </button>
    <button
      class="rounded-xl bg-light-1 px-3 py-2 dark:bg-dark-2"
      @click="() => (!panOnDrag ? (panOnDrag = true) : (panOnDrag = false))"
    >
      <em v-if="!panOnDrag" class="mdi mdi-lock-outline text-xl"></em>
      <em v-else class="mdi mdi-lock-open-outline text-xl"></em>
    </button>

    <button
      class="rounded-xl bg-light-1 px-3 py-2 dark:bg-dark-2"
      @click="() => toggleSwapMode()"
    >
      <em
        class="mdi mdi-swap-horizontal text-xl"
        :class="{ 'text-primary-2': swapMode }"
      ></em>
    </button>
  </div>
  <VueFlow
    ref="container"
    v-model:edges="edges"
    v-model:nodes="nodes"
    :class="
      useLayoutStore().isDarkMode() ? 'canvas-bg-dark' : 'canvas-bg-light'
    "
    data-testId="roomGroupCanvas"
  >
    <template #node-room="props">
      <RoomNode :data="props.data" :swap-mode="swapMode" />
    </template>
    <template #node-add-room>
      <AddRoomNode />
    </template>
    <template #edge-custom="props">
      <RoomEdge
        :id="props.id"
        :source-x="props.sourceX"
        :source-y="props.sourceY"
        :targetX="props.targetX"
        :targetY="props.targetY"
        :source-position="props.sourcePosition"
        :target-position="props.targetPosition"
        :data="props.data"
      />
    </template>
  </VueFlow>
</template>
